import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, HeadersDefaults } from 'axios'
import axios from 'axios'

export const TOKEN_EXPIRATION_MARGIN = 2 * 1000 // 2s
export const RETRY_REFRESH_INTERVAL_MS = import.meta.env.NODE_ENV === 'test' ? 20 : 1000
export const RETRY_REFRESH_MAX = 2

// const TIMEOUT_MIN = 5

export class Token {
  name = Token
  /**
   * @type {String} Token body
   */
  private readonly body: string
  /**
   * @type {Number} Timestamp in milliseconds
   */
  private readonly expiration: number

  constructor(content = '') {
    this.body = content
    this.expiration = 0
    if (content) {
      try {
        const { exp } = JSON.parse(atob(content.split('.')[1]))
        this.expiration = exp ? exp * 1000 : 0
      } catch (error) {
        this.body = String(error)
      }
    }
  }

  get timeToExpiration() {
    return this.expiration - Date.now()
  }

  /**
   * Check token will not expire within 2 secondes
   * @return {boolean}
   */
  get isUp() {
    return this.timeToExpiration > TOKEN_EXPIRATION_MARGIN
  }

  toString() {
    return this.body
  }
}

// Full config:  https://github.com/axios/axios#request-config
export const baseConfig = {
  baseURL: import.meta.env.VITE_APP_BACK_URL,
}

export const axiosWriteMethod = ['post', 'put', 'patch']

export class Authority {
  get: AxiosInstance['get']
  delete: AxiosInstance['delete']
  head: AxiosInstance['head']
  options: AxiosInstance['options']
  post: AxiosInstance['post']
  put: AxiosInstance['put']
  patch: AxiosInstance['patch']
  storage: Storage
  refreshTimeout!: number | NodeJS.Timeout
  mustRefresh: boolean
  available: boolean
  private access: Token
  private refresh: Token
  private readonly instance: AxiosInstance
  private readonly headers: HeadersDefaults['common']

  constructor(storage: Storage) {
    this.available = false // token are set and not refreshing
    this.access = new Token()
    this.refresh = new Token()

    const instance = axios.create(baseConfig)
    this.headers = instance.defaults.headers.common
    this.instance = instance

    this.get = this.refreshWrapper('get') as AxiosInstance['get']
    this.delete = this.refreshWrapper('delete') as AxiosInstance['delete']
    this.head = this.refreshWrapper('head') as AxiosInstance['head']
    this.options = this.refreshWrapper('options') as AxiosInstance['options']
    this.post = this.refreshWrapper('post') as AxiosInstance['post']
    this.put = this.refreshWrapper('put') as AxiosInstance['put']
    this.patch = this.refreshWrapper('patch') as AxiosInstance['patch']

    this.storage = storage
    this.mustRefresh = false

    this.initToken().then(() => {
      this.available = true
    })
  }

  /**
   * If the access token is up, or the refresh token is up and the access token must be refreshed, then the user is active.
   *
   * @returns A boolean value.
   */
  public get isActive(): boolean {
    return this.access.isUp || (this.refresh.isUp && this.mustRefresh)
  }

  /**
   * It returns a function that takes in the same parameters as the axios method it's wrapping, and returns a promise that
   * resolves to the same type as the axios method it's wrapping.
   * Add the tokrenRefresh function before the axios method, to refresh the access token if it's expired.
   *
   * @param axiosMethod - keyof AxiosInstance
   * @returns A function that takes in a url and a config object and returns a promise that resolves to an AxiosResponse.
   */
  refreshWrapper(axiosMethod: keyof AxiosInstance): AxiosInstance[keyof AxiosInstance] {
    if (axiosWriteMethod.includes(axiosMethod)) {
      return async <D, T, R = AxiosResponse<T>>(url: string, data?: D, config?: AxiosRequestConfig<D>): Promise<R> => {
        await this.tokenRefresh()
        return this.instance[axiosMethod as 'post' | 'put' | 'patch'](url, data, config)
      }
    }
    return async <D, T, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig<D>): Promise<R> => {
      await this.tokenRefresh()
      return this.instance[axiosMethod as 'get' | 'delete' | 'head' | 'options'](url, config)
    }
  }

  /**
   * It sets the Accept-Language header to the locale passed in, and if that locale is not available, it sets it to the
   * fallback locale
   * @param {string} locale - The locale to set the language to.
   */
  setLanguage(locale: string) {
    this.headers['Accept-Language'] = `${locale};q=0.8,fr;q=0.5`
  }

  /**
   * It sets the Authorization header to the value of the access token.
   */
  setAuthHeader() {
    this.headers.Authorization = `Bearer ${this.access}`
  }

  /**
   * It sets a timeout that will trigger a refresh of the access token.
   * When the access token is initialized, the timeout is set to the expiration time of the token with a margin of TOKEN_EXPIRATION_MARGIN sec.
   */
  setAutoRefresh() {
    const refreshTime = this.access.timeToExpiration - TOKEN_EXPIRATION_MARGIN
    this.refreshTimeout = setTimeout(() => (this.mustRefresh = true), refreshTime)
  }

  /**
   * It sets the auth header, sets the available flag to true, and sets the auto refresh.
   */
  async initAutoAuth(): Promise<void> {
    this.setAuthHeader()
    this.available = true
    this.mustRefresh = false
    this.setAutoRefresh()
  }

  /**
   * It updates the access token and then calls the initAutoAuth function.
   * Function called when the access token is expired, and after it has been refreshed.
   *
   * @param {string} access - string - the access token
   * @returns The initAutoAuth function is being returned.
   */
  async updateAccess(access: string) {
    this.access = new Token(access)
    this.storage.setItem(`access`, access)
    return this.initAutoAuth()
  }

  /**
   * It tries to refresh the access token, and if it fails, it retries up to RETRY_REFRESH_MAX times, with an interval of
   * RETRY_REFRESH_INTERVAL_MS between each attempt.
   *
   * @param [attempt=0] - The number of times we've tried to refresh the token.
   * @returns A promise that resolves when the token has been refreshed.
   */
  async tokenRefresh(attempt = 0): Promise<void> {
    if (this.mustRefresh) {
      this.available = false
      try {
        const response: AxiosResponse<{ access: string }> = await this.instance.post('account/jwt-token/refresh/', {
          refresh: this.refresh.toString(),
        })

        await this.updateAccess(response.data.access)
      } catch (error) {
        if (attempt < RETRY_REFRESH_MAX && this.refresh.isUp) {
          await (() =>
            new Promise((resolve) => {
              setTimeout(() => resolve(this.tokenRefresh(attempt + 1)), attempt * RETRY_REFRESH_INTERVAL_MS)
            }))()
        } else {
          throw error
        }
      }
    }
  }

  /**
   * It inits the access and refresh tokens and then calls initAutoAuth().
   *
   * @param {Token} access - The access token.
   * @param {Token} refresh - The refresh token
   *
   * @returns The return value of the initAutoAuth function.
   */
  async updatePair(access: Token, refresh: Token) {
    this.access = access
    this.refresh = refresh
    return this.initAutoAuth()
  }

  /**
   * Function called by the constructor of the class.
   * If the access token is up, update the pair, if not, refresh the token.
   *
   * @returns A promise that resolves to a void.
   */
  async initToken(): Promise<void> {
    this.available = false
    const access = this.storage.getItem(`access`)
    const refresh = this.storage.getItem(`refresh`)
    if (access && refresh) {
      const accessToken = new Token(access)
      const refreshToken = new Token(refresh)

      if (refreshToken.isUp) {
        if (accessToken.isUp) {
          return this.updatePair(accessToken, refreshToken)
        }

        this.refresh = refreshToken
        this.mustRefresh = true
        return this.tokenRefresh()
      }
    }
    return Promise.resolve()
  }

  /**
   * It deletes the authorization header from the axios instance, clears the storage, and sets the access token to a new
   * Token object
   */
  logOut() {
    const headers = this.instance.defaults.headers.common
    if ('Authorization' in headers) {
      delete headers.Authorization
    }
    this.mustRefresh = false
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
    }
    this.storage.clear()
    this.access = new Token()
    this.available = false
  }

  /**
   * It takes a `TicketCredential` object, and if it has a `t` property, it sends a POST request to the
   * `account/jwt-token/` endpoint with the `TicketCredential` object as the body of the request. If the request is
   * successful, it stores the `access` and `refresh` tokens in the `storage` object, and returns a promise that resolves
   * to the `updatePair` function, which takes the `access` and `refresh` tokens as arguments.
   *
   * @param {TicketCredential} credentials - TicketCredential
   * @returns A promise that resolves to a token pair.
   */
  logIn(credentials: TicketCredential) {
    const url = 'account/jwt-token/'
    if (credentials.t) {
      return this.instance.post(url, credentials).then((response) => {
        const { access, refresh } = response.data
        this.storage.setItem('access', access)
        this.storage.setItem('refresh', refresh)
        return this.updatePair(new Token(access), new Token(refresh))
      })
    }

    return Promise.reject(new TypeError('credentials provided are not valid'))
  }
}

// const authority = new Authority(localStorage)
//
// export default authority
interface TicketCredential {
  t: string
}

// Add a response interceptor
// _axios.interceptors.response.use(
//     function(response) {
//       // Do something with response data
//       return response
//     },
//     function(error) {
//       // Do something with response error
//       return Promise.reject(error)
//     }
// )

export default new Authority(localStorage)
